import {gql} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileStateAttributes} from 'filestate/FileState';
import FolderItemState from 'filestate/FolderItemState';
import ApolloFolderItemBase from 'folder-items/ApolloFolderItemBase';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';
import Preferences from 'Preferences';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {FileFileStateQuery, FileFileStateQueryVariables} from './__generated/FileFileStateQuery';
import FileTabState from './FileTabState';

type FileNodeGraphql = ExtractByTypename<FileFileStateQuery['file'], 'FileNode'>;

export const FILE_FILE_STATE_QUERY: DocumentNode = gql`
  query FileFileStateQuery($fileId: StrictString!) @skipAudit @engComponent(name: PIPELINES) {
    file(id: $fileId) {
      ... on FileNode {
        ...BaseFolderItemFileGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class File extends ApolloFolderItemBase<
  FileFileStateQuery,
  FileNodeGraphql,
  FileFileStateQueryVariables
> {
  protected getQueryWrapper(): QueryWrapper<FileFileStateQuery, FileFileStateQueryVariables> {
    return {query: FILE_FILE_STATE_QUERY};
  }

  protected queryVariables(): FileFileStateQueryVariables {
    return {fileId: this.id};
  }

  protected getGraphQLFolderItemFromQueryData(
    data: FileFileStateQuery
  ): FileNodeGraphql | InaccessibleInfoGraphQL {
    return data.file;
  }

  protected getUpdatedQueryData(attrs: Partial<FileNodeGraphql>): FileFileStateQuery {
    return {
      file: {
        ...this.graphQLFolderItem(),
        ...attrs,
      },
    };
  }
}

interface FileFileState extends FolderItemState {
  model(): File;
}

class FileFileState extends FolderItemState {
  static Model = File;
  static fileType = ModelTypeConstants.FILE;

  static initClass(): void {
    this.prototype.hasNote = false;
  }

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }

  initialize(attributes: {}, options: unknown): void {
    super.initialize(attributes, options);
    const fileTab = new FileTabState({id: this.id});
    this.buffers().at(0).tabs().add(fileTab);
    fileTab.set({active: true});
    const splitPref = Preferences.get(this.getSplitWorkspacePreferenceKey());
    this.setIsWorkspaceSplit(splitPref != null ? splitPref : false);
  }
}

FileFileState.initClass();

export default FileFileState;
